export type CrashKind = 'error' | 'unhandledrejection' | 'manual';

export interface CrashReport {
  kind: CrashKind;
  message: string;
  name?: string;
  stack?: string;
  /** epoch ms */
  ts: number;
  url?: string;
  release?: string;
  context?: Record<string, string | number | boolean>;
}

export interface CrashReporter {
  capture(error: unknown, kind?: CrashKind, context?: CrashReport['context']): void;
  /** Send whatever is queued (call on pause/pagehide). */
  flush(): Promise<void>;
}

export function toReport(error: unknown, kind: CrashKind, ts: number, extra: Pick<CrashReport, 'url' | 'release' | 'context'> = {}): CrashReport {
  const e = error instanceof Error ? error : null;
  const message = (e ? e.message : typeof error === 'string' ? error : JSON.stringify(error) ?? String(error)).slice(0, 1000);
  return {
    kind,
    message,
    ...(e?.name ? { name: e.name } : {}),
    ...(e?.stack ? { stack: e.stack.slice(0, 8000) } : {}),
    ts,
    ...Object.fromEntries(Object.entries(extra).filter(([, v]) => v !== undefined)),
  };
}

/** Fan out to several reporters (API + Sentry). */
export function teeReporter(...reporters: CrashReporter[]): CrashReporter {
  return {
    capture: (error, kind, context) => reporters.forEach((r) => r.capture(error, kind, context)),
    flush: async () => {
      await Promise.all(reporters.map((r) => r.flush()));
    },
  };
}
